import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris/index';
import Categories from './models/Categories';
import Pagination from './Pagination';

export class AppStore extends Pagination {
  @observable apps = [];

  @observable appStatus;

  @observable isLoading = false;

  @observable isSortLoading = false;

  @observable currentAppInfo = [];

  @observable currentAppStructureIds = [];

  @observable selectedStructureIds = [];

  @observable allAppList = [];

  @observable authorizeType = [];

  @observable categories = [];

  @observable appsLength;

  @observable isPurchasedAppDisplay = false;

  @observable isSelfDesignedAppDisplay = false;

  @observable appDesignEnable;

  @observable appRunEnable;

  @observable selectedAppType;

  @action.bound
  setSelectedAppType(data) {
    this.selectedAppType = data;
  }

  @action
  setAppsLength(num) {
    this.appsLength = num;
  }

  @action resetLoading(boolean) {
    this.isLoading = boolean;
  }

  @action
  resetSortLoading(boolean) {
    this.isSortLoading = boolean;
  }

  @action getCurrentAppInfo(data) {
    this.currentAppInfo = data;
  }

  @action.bound
  setCategories(data) {
    this.categories = data;
  }

  @action.bound
  setAuthType(data) {
    this.authorizeType = data;
  }

  @action.bound
  setApps(data, type) {
    if (type === '1') {
      this.apps = data;
    } else {
      this.allAppList = data;
    }
  }

  @action.bound
  setAllApps(data) {
    this.allAppList = data;
  }

  @action.bound
  setPurchasedAppDisplay(data) {
    this.isPurchasedAppDisplay = data;
  }

  @action.bound
  setSelfDesignedAppDisplay(data) {
    this.isSelfDesignedAppDisplay = data;
  }

  @action.bound
  getAppList(body = {}, type = '1') {
    this.isLoading = true;
    axiosInstance
      .post(uris.getAppList(), {...body})
      .then((data) => {
        this.setApps(data.apps || [], type);
        this.setPurchasedAppDisplay(data?.isPurchasedAppDisplay);
        this.setSelfDesignedAppDisplay(data?.isSelfDesignedAppDisplay);
        if (type === '1') {
          this.setPagination(data.pagination);
          this.setAppsLength((data.apps || []).length);
        }
        this.resetLoading(false);
      })
      .catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  getAppListWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.appsLength === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getAppList({
      ...data,
      ...body
    });
  }

  @action.bound
  setAppDesignEnable(data) {
    this.appDesignEnable = data;
  }

  @action.bound
  getAppDesignEnable() {
    axiosInstance
      .get(uris.getAppDesignEnable())
      .then((data) => {
        this.setAppDesignEnable(data);
      });
  }

  @action.bound
  setAppRunEnable(data) {
    this.appRunEnable = data;
  }

  @action.bound
  getAppRunEnable() {
    axiosInstance
      .get(uris.getAppRunEnable())
      .then((data) => {
        this.setAppRunEnable(data);
      });
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
    this.getAppListWithAddition({createdType: this.selectedAppType});
  }

  @action.bound
  updateAppStatusFromServer(data) {
    const app = this.apps.find(({id}) => id === data.id);
    app.status = !data.status;
  }

  @action.bound
  updateAppNewNavigationFromServer(data) {
    const app = this.apps.find(({id}) => id === data.id);
    app.isNewNavigation = !data.isNewNavigation;
  }

  @action.bound
  setAppStatus(appId) {
    const app = this.apps.find(({id}) => id === appId);
    const newStatus = !app.status;
    axiosInstance
      .post(uris.setAppStatus(), {
        appId: app.id,
        enable: newStatus
      })
      .then(() => {
        this.updateAppStatusFromServer(app);
      });
  }

  @action.bound
  setNewNavigation(appId, isNewNavigation) {
    const app = this.apps.find(({id}) => id === appId);
    axiosInstance
      .post(uris.setNewNavigation(), {
        appId: app.id,
        isNewNavigation
      })
      .then(() => {
        this.updateAppNewNavigationFromServer(app);
      });
  }

  @action.bound
  getAppDetail(appId) {
    this.resetLoading(true);
    axiosInstance
      .get(uris.getAppDetail(), {
        params: {
          id: appId
        }
      })
      .then((data) => {
        this.getCurrentAppInfo(data);
        this.resetLoading(false);
      })
      .catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  assginAppStructure(appId, structureIds) {
    axiosInstance
      .post(uris.assignAppStructure(), {
        appId,
        structureIds
      })
      .then(() => {
        message.success(localeText('APP.HINT_ASSIGN_APP_STRUCTURE'));
        this.getAppListWithAddition({createdType: this.selectedAppType});
      });
  }

  @action.bound
  getCurrentAppStructures(appId) {
    const app = this.apps.find(({id}) => id === appId);
    this.currentAppStructureIds = app.structures;
  }

  @action.bound
  setSelectedStructureIds(data) {
    this.selectedStructureIds = data;
  }

  @action.bound
  resetSelectedStructureIds() {
    this.selectedStructureIds = this.currentAppStructureIds;
  }

  @action.bound
  deleteShortcut(appId) {
    this.resetLoading(true);
    axiosInstance
      .post(
        uris.deleteShortcut(),
        {
          appId
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_DELETE_FAIL')
          }
        }
      )
      .then(() => {
        message.success(localeText('APP.MSG_DELETE_SHORTCUT_SUCCESS'));
        this.resetLoading(false);
        this.getAppListWithAddition({isDelete: true});
      })
      .catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  getAppAuthorizationInfo(appId) {
    this.resetLoading(true);
    return axiosInstance
      .get(uris.getAppAuthorizationInfo(), {
        params: {appId}
      })
      .then((data) => {
        this.setAuthType(data);
        this.resetLoading(false);
        return data;
      })
      .catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  setAppAuthorization(appId, dataAuthorizeType) {
    this.resetLoading(true);
    axiosInstance
      .post(uris.setAppAuthorization(), {
        appId,
        dataAuthorizeType
      })
      .then(() => {
        message.success(localeText('APP.MSG_API_AUTHORIZATION_SUCCESS'));
        this.resetLoading(false);
      })
      .catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  getAppListWithOrder(body = {}) {
    this.resetSortLoading(true);
    axiosInstance
      .post(uris.getAppListWithOrder(), {
        ...body
      })
      .then((data) => {
        this.setCategories((data.categories || []).map(json => this.updateCategoriesFromServer(this.categories, json)));
        this.resetSortLoading(false);
      }).catch(() => {
        this.resetSortLoading(false);
      });
  }

  @action.bound
  setAppListWithOrder(categories) {
    this.resetLoading(true);
    axiosInstance
      .post(uris.setAppListWithOrder(), {
        categories
      })
      .then(() => {
        message.success(localeText('APP.HINT_SORT_APP_SUCCESS'));
        this.getAppListWithAddition({forceFresh: true, createdType: this.selectedAppType});
        this.getAppListWithOrder();
        this.resetLoading(false);
      }).catch(() => {
        this.resetLoading(false);
      });
  }

  @action.bound
  updateCategoriesFromServer(arr, json) {
    let categorie = arr.find(({id}) => id === json.id);
    if (!categorie) {
      categorie = new Categories(this, json);
    }
    categorie.updateFromJson(json);
    return categorie;
  }
}

export default new AppStore();
